import { useMemo, useState } from 'react';
import { MessageSquare, Send, Loader2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  AdminPage, EmptyState, PersonAvatar, adminButton, useAdminPageTitle,
} from '@/components/admin/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { useCleanerTeamMember } from '@/hooks/useCleanerTeamMemberId';
import { useMessagingRecipients, useMessageThread, type TeamMessage } from '@/hooks/useTeamMessaging';

const formatTimestamp = (iso: string) => {
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MessagesAdmin = () => {
  useAdminPageTitle('Messages');
  const { user, staffRole } = useAuth();
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();

  const isCleaner = staffRole === 'cleaner';
  const isManager = staffRole === 'owner' || staffRole === 'supervisor' || staffRole === 'super_admin';
  const senderType: 'org' | 'cleaner' = isCleaner ? 'cleaner' : 'org';
  const senderName = (user?.user_metadata?.name as string | undefined) || user?.email || 'You';

  // Cleaners message from their own team_members row; managers pick a recipient.
  const { data: cleanerTeamMember } = useCleanerTeamMember(orgId, isCleaner);
  const { data: recipients = [], isLoading: recipientsLoading } = useMessagingRecipients(orgId, isManager);
  const [selectedRecipientId, setSelectedRecipientId] = useState<string | null>(null);

  const activeOrgId = isCleaner ? cleanerTeamMember?.organizationId ?? orgId : orgId;
  const activeTeamMemberId = isCleaner ? cleanerTeamMember?.id ?? null : selectedRecipientId;

  const { messages, isLoading, sendMessage } = useMessageThread({
    orgId: activeOrgId,
    teamMemberId: activeTeamMemberId,
    senderType,
    senderUserId: user?.id,
    senderName,
    canWrite,
  });

  const unreadCount = useMemo(
    () => messages.filter((m) => m.sender_type !== senderType && !m.read_at).length,
    [messages, senderType],
  );

  const [draft, setDraft] = useState('');
  const handleSend = () => {
    if (!assertCanWrite()) return;
    if (!draft.trim() || sendMessage.isPending) return;
    sendMessage.mutate(draft, { onSuccess: () => setDraft('') });
  };

  const selectedRecipient = recipients.find((r) => r.id === selectedRecipientId) ?? null;
  const threadTitle = isCleaner
    ? 'Your team'
    : selectedRecipient
      ? selectedRecipient.name
      : 'Select a team member';

  const canCompose = Boolean(activeTeamMemberId);

  return (
    <AdminPage>
      <p className="-mb-1 text-[13.5px] text-muted-foreground">
        {isCleaner
          ? 'Chat with your team and reply to messages from your organisation.'
          : 'Send and receive messages with your team members.'}
      </p>

      <div
        className={cn(
          'adm-panel grid min-w-0 overflow-hidden',
          isManager ? 'lg:grid-cols-[300px_1fr]' : 'grid-cols-1',
        )}
      >
        {isManager && (
          <aside className="flex min-w-0 flex-col border-b border-border lg:border-b-0 lg:border-r">
            <div className="flex items-center justify-between gap-2 px-4 pb-2 pt-4">
              <h2 className="flex items-center gap-2 text-[14px] font-semibold text-foreground">
                <Users className="h-4 w-4 text-muted-foreground" />
                Team members
              </h2>
              {recipients.length > 0 && <span className="adm-filter-count">{recipients.length}</span>}
            </div>
            <div className="adm-scroll max-h-[260px] space-y-0.5 overflow-y-auto p-2 pt-1 lg:max-h-[calc(100vh-240px)]">
              {recipientsLoading ? (
                <div className="flex items-center gap-2 px-2 py-3 text-[13px] text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : recipients.length === 0 ? (
                <p className="px-2 py-3 text-[13px] text-muted-foreground">No team members yet.</p>
              ) : (
                recipients.map((recipient) => (
                  <button
                    key={recipient.id}
                    type="button"
                    data-testid={`message-recipient-${recipient.id}`}
                    data-active={recipient.id === selectedRecipientId}
                    onClick={() => setSelectedRecipientId(recipient.id)}
                    className="adm-row py-2"
                  >
                    <PersonAvatar name={recipient.name} seed={recipient.id} size={34} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-semibold text-foreground">{recipient.name}</span>
                      {recipient.email && (
                        <span className="block truncate text-[12px] text-muted-foreground">{recipient.email}</span>
                      )}
                    </span>
                  </button>
                ))
              )}
            </div>
          </aside>
        )}

        <section className="flex min-h-[480px] min-w-0 flex-col lg:h-[calc(100vh-200px)] lg:max-h-[760px]">
          <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 md:px-5">
            <div className="flex min-w-0 items-center gap-3">
              {selectedRecipient ? (
                <PersonAvatar name={selectedRecipient.name} seed={selectedRecipient.id} size={36} />
              ) : (
                <span className="adm-icon-tile" aria-hidden="true">
                  <MessageSquare />
                </span>
              )}
              <div className="min-w-0">
                <h2 className="truncate text-[14px] font-semibold text-foreground">{threadTitle}</h2>
                <p className="truncate text-[12px] text-muted-foreground">
                  {selectedRecipient?.email || (isCleaner ? 'Messages with your organisation' : 'Direct messages')}
                </p>
              </div>
            </div>
            {unreadCount > 0 && (
              <span data-testid="messages-unread-badge" className="adm-chip adm-chip--violet adm-chip--dot">
                {unreadCount} unread
              </span>
            )}
          </header>

          <div className="adm-scroll flex-1 space-y-4 overflow-y-auto bg-muted/30 px-4 py-5 md:px-6" data-testid="message-list">
            {!canCompose ? (
              <EmptyState
                icon={<MessageSquare />}
                title={isManager ? 'Select a team member to start messaging.' : 'You are not set up as a team member yet.'}
                description={isManager ? 'Pick someone from the list to open your conversation.' : undefined}
              />
            ) : isLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <EmptyState icon={<MessageSquare />} title="No messages yet." description="Say hello — your first message starts the thread." />
            ) : (
              messages.map((message) => (
                <MessageBubble key={message.id} message={message} mine={message.sender_type === senderType} />
              ))
            )}
          </div>

          <div className="border-t border-border p-3 md:px-5">
            <div className="flex items-end gap-2 rounded-xl border border-input bg-card p-1.5 pl-3 shadow-[var(--adm-shadow-xs)] focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/15">
              <Textarea
                aria-label="Message"
                placeholder={canCompose ? 'Type a message…' : 'Select a recipient to send a message'}
                value={draft}
                disabled={!canWrite || !canCompose || sendMessage.isPending}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                className="min-h-[36px] resize-none border-0 bg-transparent px-0 py-2 text-[13.5px] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:bg-transparent"
                rows={1}
              />
              <Button
                type="button"
                size="sm"
                onClick={handleSend}
                disabled={!canWrite || !canCompose || !draft.trim() || sendMessage.isPending}
                className={cn(adminButton.primary, 'shrink-0')}
              >
                {sendMessage.isPending ? <Loader2 className="animate-spin" /> : <Send />}
                Send
              </Button>
            </div>
            <p className="mt-1.5 hidden px-1 text-[11px] text-muted-foreground sm:block">
              Enter to send · Shift + Enter for a new line
            </p>
          </div>
        </section>
      </div>
    </AdminPage>
  );
};

const MessageBubble = ({ message, mine }: { message: TeamMessage; mine: boolean }) => {
  const name = message.sender_name || (mine ? 'You' : 'Team');
  return (
    <div data-testid="message-item" className={cn('flex items-end gap-2.5', mine ? 'flex-row-reverse' : 'flex-row')}>
      <PersonAvatar name={name} seed={mine ? null : name} size={28} className="mb-5" />
      <div className={cn('flex min-w-0 max-w-[78%] flex-col gap-1', mine ? 'items-end' : 'items-start')}>
        <div
          className={cn(
            'rounded-2xl px-3.5 py-2 text-[13.5px] leading-relaxed shadow-[var(--adm-shadow-xs)]',
            mine
              ? 'rounded-br-md bg-primary text-primary-foreground'
              : 'rounded-bl-md border border-border bg-card text-foreground',
          )}
        >
          <p className="whitespace-pre-wrap break-words">{message.body}</p>
        </div>
        <span className="px-1 text-[11px] text-muted-foreground">
          {name} · {formatTimestamp(message.created_at)}
        </span>
      </div>
    </div>
  );
};

export default MessagesAdmin;
